import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { Volume2, VolumeX, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/utils/cn';
import { useSerenePreferences } from '@/providers/serene-provider';
import { DEFAULT_AUDIO_TRACKS } from '../utils/serene-audio-tracks';

// ============================================================================
// Types
// ============================================================================

/**
 * Represents an audio track available for playback
 */
export interface AudioTrack {
  /** Unique identifier for the track */
  id: string;
  /** Display name for the track */
  name: string;
  /** URL to the audio file (OGG preferred for seamless looping) */
  url: string;
  /** Optional duration in seconds for display purposes */
  duration?: number;
}

/**
 * Props for the SereneAudioPlayer component
 */
export interface SereneAudioPlayerProps {
  /** Available audio tracks to choose from */
  tracks?: AudioTrack[];
  /** ID of the default track to play */
  defaultTrack?: string;
  /** Whether to show the control UI */
  showControls?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Optional children to render alongside controls */
  children?: ReactNode;
}

/**
 * Audio playback state managed by the component
 */
interface AudioState {
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Whether audio is currently loading */
  isLoading: boolean;
  /** Whether audio failed to load */
  hasError: boolean;
  /** Error message if load failed */
  errorMessage: string | null;
  /** Current volume level (0-1) */
  volume: number;
  /** Whether audio is muted */
  isMuted: boolean;
  /** Currently selected track ID */
  selectedTrackId: string | null;
}

/** localStorage key for persisting audio preferences */
const AUDIO_STORAGE_KEY = 'serene-audio-preferences';

/** Default volume level (0-1) */
const DEFAULT_VOLUME = 0.5;

// ============================================================================
// Utilities
// ============================================================================

/**
 * Load audio preferences from localStorage
 */
function loadAudioPreferences(): {
  volume: number;
  selectedTrackId: string | null;
} {
  if (typeof window === 'undefined') {
    return { volume: DEFAULT_VOLUME, selectedTrackId: null };
  }

  try {
    const stored = localStorage.getItem(AUDIO_STORAGE_KEY);
    if (!stored) {
      return { volume: DEFAULT_VOLUME, selectedTrackId: null };
    }
    const parsed = JSON.parse(stored);
    return {
      volume:
        typeof parsed.volume === 'number'
          ? Math.max(0, Math.min(1, parsed.volume))
          : DEFAULT_VOLUME,
      selectedTrackId:
        typeof parsed.selectedTrackId === 'string'
          ? parsed.selectedTrackId
          : null,
    };
  } catch {
    return { volume: DEFAULT_VOLUME, selectedTrackId: null };
  }
}

/**
 * Save audio preferences to localStorage
 */
function saveAudioPreferences(volume: number, selectedTrackId: string | null) {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(
      AUDIO_STORAGE_KEY,
      JSON.stringify({ volume, selectedTrackId })
    );
  } catch {
    console.warn('Failed to save audio preferences');
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * SereneAudioPlayer - Ambient audio player for serene breathing exercises
 *
 * Uses the Web Audio API for seamless looping and crossfading.
 * Integrates with SereneProvider for respecting user preferences.
 *
 * Features:
 * - Seamless audio looping using Web Audio API
 * - Volume control with accessible slider
 * - Mute/unmute toggle with aria-pressed
 * - Multiple audio track selection
 * - Auto-pause when tab becomes inactive (optional)
 * - Graceful error handling for audio load failures
 * - Screen reader announcements for state changes
 * - Persists preferences to localStorage
 *
 * @example
 * ```tsx
 * <SereneAudioPlayer
 *   tracks={[
 *     { id: 'rain', name: 'Rain', url: '/audio/rain.ogg' },
 *     { id: 'waves', name: 'Ocean', url: '/audio/ocean.ogg' },
 *   ]}
 *   defaultTrack="rain"
 * />
 * ```
 */
export function SereneAudioPlayer({
  tracks = DEFAULT_AUDIO_TRACKS,
  defaultTrack,
  showControls = true,
  className,
  children,
}: SereneAudioPlayerProps) {
  // Get serene preferences from context
  const { preferences, updatePreference } = useSerenePreferences();

  // Audio context and nodes refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  // Load initial preferences from localStorage
  const [audioState, setAudioState] = useState<AudioState>(() => {
    const stored = loadAudioPreferences();
    return {
      isPlaying: false,
      isLoading: false,
      hasError: false,
      errorMessage: null,
      volume: stored.volume,
      isMuted: !preferences.audioEnabled,
      selectedTrackId: defaultTrack ?? stored.selectedTrackId ?? tracks[0]?.id ?? null,
    };
  });

  // Screen reader announcement ref
  const announcementRef = useRef<HTMLDivElement>(null);

  /**
   * Announce state changes to screen readers
   */
  const announce = useCallback((message: string) => {
    if (announcementRef.current) {
      announcementRef.current.textContent = message;
    }
  }, []);

  /**
   * Initialize or get the AudioContext
   * Attempts to resume if suspended (browser autoplay policy)
   */
  const getAudioContext = useCallback((): AudioContext | null => {
    if (audioContextRef.current) {
      // Try to resume if suspended (happens due to browser autoplay policy)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {
          // Silently handle - we'll try again in startPlayback
        });
      }
      return audioContextRef.current;
    }

    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;

      if (!AudioContextClass) {
        console.warn('Web Audio API not supported');
        return null;
      }

      audioContextRef.current = new AudioContextClass();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
      gainNodeRef.current.gain.value = audioState.isMuted ? 0 : audioState.volume;

      // Immediately try to resume the context
      // This works when called from a user gesture context
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {
          // Silently handle - we'll try again in startPlayback
        });
      }

      return audioContextRef.current;
    } catch (error) {
      console.error('Failed to create AudioContext:', error);
      return null;
    }
  }, [audioState.isMuted, audioState.volume]);

  /**
   * Load an audio file into a buffer
   */
  const loadAudioBuffer = useCallback(
    async (url: string): Promise<AudioBuffer | null> => {
      const context = getAudioContext();
      if (!context || !url) return null;

      try {
        setAudioState((prev) => ({
          ...prev,
          isLoading: true,
          hasError: false,
          errorMessage: null,
        }));

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await context.decodeAudioData(arrayBuffer);

        setAudioState((prev) => ({
          ...prev,
          isLoading: false,
        }));

        return audioBuffer;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load audio';
        console.error('Audio load error:', errorMessage);

        setAudioState((prev) => ({
          ...prev,
          isLoading: false,
          hasError: true,
          errorMessage,
        }));

        announce(`Audio failed to load: ${errorMessage}`);
        return null;
      }
    },
    [getAudioContext, announce]
  );

  /**
   * Start playing the audio buffer in a loop
   */
  const startPlayback = useCallback(() => {
    const context = audioContextRef.current;
    const buffer = audioBufferRef.current;

    if (!context || !buffer) return;

    // Stop any existing playback
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
        sourceNodeRef.current.disconnect();
      } catch {
        // Ignore errors when stopping
      }
    }

    // Resume context if suspended (required for mobile)
    if (context.state === 'suspended') {
      context.resume();
    }

    // Create new source node
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gainNodeRef.current!);
    source.start(0);

    sourceNodeRef.current = source;
  }, []);

  // Ref to store the current volume for use in callbacks without stale closure issues
  const volumeRef = useRef(audioState.volume);
  useEffect(() => {
    volumeRef.current = audioState.volume;
  }, [audioState.volume]);

  /**
   * Stop audio playback immediately (with visual fade for UX)
   * CRITICAL: Source is stopped immediately to prevent race conditions.
   * The gain fade is purely cosmetic and doesn't delay the actual stop.
   */
  const stopPlayback = useCallback(() => {
    const context = audioContextRef.current;
    const gainNode = gainNodeRef.current;
    const source = sourceNodeRef.current;

    if (!source) return;

    // Stop source IMMEDIATELY to prevent race conditions
    // This is the critical fix - don't wait for fade
    try {
      source.stop();
      source.disconnect();
    } catch {
      // Source might already be stopped
    }
    sourceNodeRef.current = null;

    // Reset gain for next playback (do this synchronously)
    if (gainNode) {
      gainNode.gain.cancelScheduledValues(context?.currentTime ?? 0);
      gainNode.gain.value = audioState.isMuted ? 0 : volumeRef.current;
    }
  }, [audioState.isMuted]);

  /**
   * Toggle play/pause
   */
  const togglePlayback = useCallback(async () => {
    const selectedTrack = tracks.find((t) => t.id === audioState.selectedTrackId);

    // Handle silence track - ALWAYS stop any playing audio
    if (!selectedTrack || selectedTrack.id === 'silence' || !selectedTrack.url) {
      stopPlayback(); // Ensure any playing audio is stopped
      setAudioState((prev) => ({
        ...prev,
        isPlaying: false,
      }));
      announce('Silence selected - no audio playing');
      return;
    }

    if (audioState.isPlaying) {
      stopPlayback();
      setAudioState((prev) => ({ ...prev, isPlaying: false }));
      announce('Audio paused');
    } else {
      // Load buffer if not already loaded or if track changed
      if (!audioBufferRef.current) {
        const buffer = await loadAudioBuffer(selectedTrack.url);
        if (!buffer) return;
        audioBufferRef.current = buffer;
      }

      startPlayback();
      setAudioState((prev) => ({ ...prev, isPlaying: true }));
      announce(`Playing ${selectedTrack.name}`);
    }
  }, [
    tracks,
    audioState.selectedTrackId,
    audioState.isPlaying,
    loadAudioBuffer,
    startPlayback,
    stopPlayback,
    announce,
  ]);

  /**
   * Handle volume change
   */
  const handleVolumeChange = useCallback(
    (newVolume: number[]) => {
      const volume = newVolume[0];
      setAudioState((prev) => ({
        ...prev,
        volume,
        isMuted: volume === 0,
      }));

      // Update gain node
      if (gainNodeRef.current && audioContextRef.current) {
        const currentTime = audioContextRef.current.currentTime;
        gainNodeRef.current.gain.setValueAtTime(
          gainNodeRef.current.gain.value,
          currentTime
        );
        gainNodeRef.current.gain.linearRampToValueAtTime(
          volume,
          currentTime + 0.1
        );
      }

      // Save preference
      saveAudioPreferences(volume, audioState.selectedTrackId);

      // Update serene preferences
      if (volume > 0) {
        updatePreference('audioEnabled', true);
      }
    },
    [audioState.selectedTrackId, updatePreference]
  );

  /**
   * Toggle mute - uses IMMEDIATE value change for instant response
   */
  const toggleMute = useCallback(() => {
    const newMuted = !audioState.isMuted;
    setAudioState((prev) => ({ ...prev, isMuted: newMuted }));

    // Update gain node IMMEDIATELY (no ramp for instant mute/unmute response)
    if (gainNodeRef.current) {
      // Cancel any scheduled changes to ensure immediate effect
      if (audioContextRef.current) {
        gainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
      }
      // Use immediate value assignment instead of ramp
      // Read from ref to avoid stale closure issues
      gainNodeRef.current.gain.value = newMuted ? 0 : volumeRef.current;
    }

    // Update serene preferences
    updatePreference('audioEnabled', !newMuted);
    announce(newMuted ? 'Audio muted' : 'Audio unmuted');
  }, [audioState.isMuted, updatePreference, announce]);

  /**
   * Handle track selection
   * CRITICAL: Always stops any playing audio before loading new track
   */
  const handleTrackSelect = useCallback(
    async (trackId: string) => {
      const wasPlaying = audioState.isPlaying;

      // ALWAYS stop current playback - this ensures silence when switching to silence track
      // and prevents race conditions when switching between audio tracks
      stopPlayback();

      // Clear buffer to force reload
      audioBufferRef.current = null;

      setAudioState((prev) => ({
        ...prev,
        selectedTrackId: trackId,
        isPlaying: false,
        hasError: false,
        errorMessage: null,
      }));

      // Save preference
      saveAudioPreferences(audioState.volume, trackId);

      const selectedTrack = tracks.find((t) => t.id === trackId);
      announce(`Selected ${selectedTrack?.name ?? 'Unknown track'}`);

      // Resume playback if was playing and new track has audio (not silence)
      if (wasPlaying && selectedTrack && selectedTrack.url && selectedTrack.id !== 'silence') {
        // Small delay to ensure Web Audio cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 50));

        const buffer = await loadAudioBuffer(selectedTrack.url);
        if (buffer) {
          audioBufferRef.current = buffer;
          startPlayback();
          setAudioState((prev) => ({ ...prev, isPlaying: true }));
        }
      }
    },
    [
      audioState.isPlaying,
      audioState.volume,
      tracks,
      stopPlayback,
      loadAudioBuffer,
      startPlayback,
      announce,
    ]
  );

  // Handle tab visibility changes - auto-pause when tab inactive
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && audioState.isPlaying) {
        // Lower volume when tab is hidden
        if (gainNodeRef.current && audioContextRef.current) {
          const currentTime = audioContextRef.current.currentTime;
          gainNodeRef.current.gain.setValueAtTime(
            gainNodeRef.current.gain.value,
            currentTime
          );
          gainNodeRef.current.gain.linearRampToValueAtTime(
            0.1,
            currentTime + 0.3
          );
        }
      } else if (!document.hidden && audioState.isPlaying) {
        // Restore volume when tab is visible
        if (gainNodeRef.current && audioContextRef.current) {
          const currentTime = audioContextRef.current.currentTime;
          const targetVolume = audioState.isMuted ? 0 : audioState.volume;
          gainNodeRef.current.gain.setValueAtTime(
            gainNodeRef.current.gain.value,
            currentTime
          );
          gainNodeRef.current.gain.linearRampToValueAtTime(
            targetVolume,
            currentTime + 0.3
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [audioState.isPlaying, audioState.isMuted, audioState.volume]);

  // Sync with serene preferences on mount
  useEffect(() => {
    if (!preferences.audioEnabled && !audioState.isMuted) {
      setAudioState((prev) => ({ ...prev, isMuted: true }));
      if (gainNodeRef.current) {
        gainNodeRef.current.gain.value = 0;
      }
    }
  }, [preferences.audioEnabled, audioState.isMuted]);

  // Auto-play when audio is enabled in preferences
  // Use refs to track state for auto-play logic without causing re-renders
  const hasAutoPlayedRef = useRef(false);
  const lastTrackIdRef = useRef<string | null>(null);

  // Keep a stable reference to togglePlayback for use in effects
  const togglePlaybackRef = useRef(togglePlayback);
  useEffect(() => {
    togglePlaybackRef.current = togglePlayback;
  }, [togglePlayback]);

  useEffect(() => {
    // Reset auto-play flag when track changes (user selected a new technique)
    if (audioState.selectedTrackId !== lastTrackIdRef.current) {
      lastTrackIdRef.current = audioState.selectedTrackId;
      hasAutoPlayedRef.current = false;
    }

    // Guard conditions - only auto-play when all conditions are met
    const shouldAutoPlay =
      preferences.audioEnabled &&
      audioState.selectedTrackId &&
      audioState.selectedTrackId !== 'silence' &&
      !audioState.isPlaying &&
      !audioState.hasError &&
      !audioState.isLoading &&
      !hasAutoPlayedRef.current;

    if (!shouldAutoPlay) {
      return;
    }

    // Mark that we're attempting auto-play for this track
    hasAutoPlayedRef.current = true;

    // Small delay to ensure audio context is ready
    // This helps with browser autoplay policies as the user has already
    // interacted with the page (toggled audio ON in settings)
    const timer = setTimeout(() => {
      togglePlaybackRef.current();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    preferences.audioEnabled,
    audioState.selectedTrackId,
    audioState.isPlaying,
    audioState.hasError,
    audioState.isLoading,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.stop();
          sourceNodeRef.current.disconnect();
        } catch {
          // Ignore cleanup errors
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Get selected track info
  const selectedTrack = tracks.find((t) => t.id === audioState.selectedTrackId);
  const isSilence =
    !selectedTrack || selectedTrack.id === 'silence' || !selectedTrack.url;

  if (!showControls) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg bg-[hsl(var(--serene-bg-secondary)_/_0.6)] px-4 py-2 backdrop-blur-sm',
        className
      )}
      role="region"
      aria-label="Ambient audio controls"
    >
      {/* Screen reader announcements */}
      <div
        ref={announcementRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      {/* Play/Mute button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={isSilence ? toggleMute : togglePlayback}
        aria-pressed={isSilence ? !audioState.isMuted : audioState.isPlaying}
        aria-label={
          isSilence
            ? audioState.isMuted
              ? 'Unmute audio'
              : 'Mute audio'
            : audioState.isPlaying
              ? 'Pause audio'
              : 'Play audio'
        }
        disabled={audioState.isLoading}
        className="text-[hsl(var(--serene-text-primary)_/_0.8)] hover:bg-[hsl(var(--serene-interactive-hover)_/_0.3)] hover:text-[hsl(var(--serene-text-primary))]"
      >
        {audioState.isMuted || !audioState.isPlaying ? (
          <VolumeX className="size-5" />
        ) : (
          <Volume2 className="size-5" />
        )}
      </Button>

      {/* Volume slider */}
      <div className="flex min-w-[100px] items-center gap-2">
        <Slider
          value={[audioState.isMuted ? 0 : audioState.volume]}
          min={0}
          max={1}
          step={0.01}
          onValueChange={handleVolumeChange}
          aria-label="Volume"
          aria-valuenow={Math.round(
            (audioState.isMuted ? 0 : audioState.volume) * 100
          )}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${Math.round((audioState.isMuted ? 0 : audioState.volume) * 100)}%`}
          variant="serene"
          className="w-full"
        />
      </div>

      {/* Track selector dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="min-w-[120px] justify-between gap-1 text-[hsl(var(--serene-text-primary)_/_0.8)] hover:bg-[hsl(var(--serene-interactive-hover)_/_0.3)] hover:text-[hsl(var(--serene-text-primary))]"
            aria-label={`Current sound: ${selectedTrack?.name ?? 'None'}. Click to change.`}
          >
            <span className="truncate text-sm">
              {selectedTrack?.name ?? 'Select sound'}
            </span>
            <ChevronDown className="size-4 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[160px]"
          aria-label="Available ambient sounds"
        >
          {tracks.map((track) => (
            <DropdownMenuItem
              key={track.id}
              onClick={() => handleTrackSelect(track.id)}
              className="flex items-center justify-between gap-2"
              aria-selected={track.id === audioState.selectedTrackId}
            >
              <span>{track.name}</span>
              {track.id === audioState.selectedTrackId && (
                <Check className="size-4" aria-hidden="true" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Error indicator with user-friendly messages */}
      {audioState.hasError && (
        <button
          onClick={togglePlayback}
          className="flex items-center gap-1 rounded bg-[hsl(var(--serene-bg-secondary)_/_0.5)] px-2 py-1 text-xs text-[hsl(var(--serene-accent-primary))] transition-colors hover:bg-[hsl(var(--serene-interactive-hover)_/_0.4)]"
          role="alert"
          aria-live="assertive"
        >
          <span>
            {audioState.errorMessage?.includes('autoplay') ||
            audioState.errorMessage?.includes('user gesture')
              ? 'Tap to enable audio'
              : audioState.errorMessage?.includes('fetch') ||
                  audioState.errorMessage?.includes('404')
                ? 'Audio file unavailable'
                : 'Tap to retry audio'}
          </span>
        </button>
      )}

      {/* Loading indicator */}
      {audioState.isLoading && (
        <span className="text-xs text-[hsl(var(--serene-text-secondary))]" aria-live="polite">
          Loading...
        </span>
      )}

      {children}
    </div>
  );
}

SereneAudioPlayer.displayName = 'SereneAudioPlayer';
